/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { ILinkStyleProps, ILinkStyles } from '@fluentui/react/lib/Link';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type CluProjectsTableProps = TableProps<CluProjectStore> & { onProjectClick: (projectId: string) => void };

const messages = defineMessages({
    name: { id: 'clu.cluProjectsTable.name', defaultMessage: 'Name' },
    language: { id: 'clu.cluProjectsTable.language', defaultMessage: 'Language' },
    lastModified: { id: 'clu.cluProjectsTable.lastModified', defaultMessage: 'Last modified' }
});

const cluProjectsTableTheme = getScopedTheme('CluProjectsTable');
const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { color: cluProjectsTableTheme.projectTypeIconColor, fontSize: '16px', marginRight: '8px' }
});
const nameLinkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({
    root: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: 'calc(100% - 24px)' }
});

/**
 * Displays a table of all extraction projects.
 */
export const CluProjectsTable = observer((props: CluProjectsTableProps) => {
    const { selection, items, onProjectClick, sortKey, ...rest } = props;
    const { rootStore } = useGlobalState();
    const { localize, locale } = useLocalization();

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 300,
            maxWidth: 500,
            isRowHeader: true,
            isResizable: true,
            fieldName: 'name',
            name: localize(messages.name),
            isSorted: sortKey?.key === 'name',
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc',
            onRender: (item: CluProjectStore) => [
                <Icon iconName="Page" styles={iconStyles} />,
                <NameCellLink styles={nameLinkStyles} onClick={() => onProjectClick(item.id)}>
                    {item.name}
                </NameCellLink>
            ]
        },
        {
            minWidth: 300,
            maxWidth: 300,
            isResizable: true,
            key: 'modifiedDate',
            fieldName: 'modifiedDate',
            name: localize(messages.lastModified),
            isSorted: sortKey?.key === 'modifiedDate',
            isSortedDescending: sortKey?.key === 'modifiedDate' && sortKey?.order === 'desc',
            onRender: (item: CluProjectStore) => (
                <LuisText variant="medium">{item.modifiedDate?.toLocaleDateString(locale) ?? '-'}</LuisText>
            )
        },
        {
            minWidth: 300,
            maxWidth: 300,
            key: 'culture',
            isResizable: true,
            fieldName: 'culture',
            name: localize(messages.language),
            isSorted: sortKey?.key === 'culture',
            isSortedDescending: sortKey?.key === 'culture' && sortKey?.order === 'desc',
            onRender: (item: CluProjectStore) =>
                (item.projectType === 'Conversation'
                    ? rootStore.cluConversationCultureStoreHouse
                    : rootStore.cluOrchestrationCultureStoreHouse
                ).items.find(l => l.code === item.culture).name
        }
    ];

    return <Table<CluProjectStore> items={items} columns={columns} selection={selection} {...rest} />;
});
